import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  NgZone,
  ViewChild,
} from '@angular/core';
import { AppState } from '../../store/app.state';
import { Store } from '@ngrx/store';
import { AuthActions } from '../../store/state/auth/auth.action-types';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe, NgIf } from '@angular/common';
import { AppsMenuComponent, PartnerAppSelection, TruncateDirective, UiDropdownComponent, UiAvatarComponent } from '@ui';
import { filter, take } from 'rxjs/operators';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { userFeature } from '../../store/state/user/user.feature';
import { organizationFeature } from '../../store/state/organization/organization.feature';
import { PermissionModels } from '@share-models';
import { RbacOrgAllowDirective } from '../../directives/rbac-org-allow.directive';
import { routerSegments } from '../../consts/routes';
import { UniversalLoginService } from '../../services/universal-login.service';
import {
  AnalyticsService,
  resolveAppSwitchAnalyticsEvent,
  SHARED_ANALYTICS_EVENTS,
  SharedAnalyticsEvent,
} from '@share-analytics';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltip,
    AsyncPipe,
    NgIf,
    UiDropdownComponent,
    UiAvatarComponent,
    RouterLink,
    TranslatePipe,
    TruncateDirective,
    AppsMenuComponent,
    UiAvatarComponent,
    RbacOrgAllowDirective,
    RouterLinkActive,
  ],
})
export class SidebarComponent implements AfterViewInit {
  public store$ = inject(Store<AppState>);
  public ngZone = inject(NgZone);
  public universalLoginService = inject(UniversalLoginService);
  private readonly analyticsService = inject(AnalyticsService);

  @ViewChild('userMenu')
  public userMenu!: ElementRef<HTMLElement>;
  @ViewChild('appsMenu')
  public appsMenu!: ElementRef<HTMLElement>;
  @ViewChild('orgMenu')
  public orgMenu!: ElementRef<HTMLElement>;

  public userMenuHeight = 0;
  public appsMenuHeight = 0;
  public orgMenuHeight = 0;
  public selectUser$ = this.store$.select(userFeature.selectProfile);
  public isDeveloper$ = this.store$.select(userFeature.selectIsDeveloper);

  public readonly appId = environment.appId;
  public readonly webUrl = environment.apps.webUrl;
  public readonly controlCenterUrl = environment.apps.controlCenterUrl;
  public readonly parkingUrl = environment.apps.parkingUrl;
  public readonly sitePlannerUrl = environment.apps.sitePlannerUrl;
  public readonly panicUrl = environment.apps.panicUrl;
  public readonly chatUrl = environment.apps.chatUrl;
  public readonly permissions = PermissionModels.Permissions;
  public readonly routerSegments = routerSegments;

  public readonly selectActiveOrganization$ = this.store$.select(
    organizationFeature.selectActiveOrganizationDocument,
  );

  public ngAfterViewInit(): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      if (this.userMenu) {
        this.userMenuHeight = this.userMenu?.nativeElement?.clientHeight ?? 0;
      }
      if (this.appsMenu) {
        this.appsMenuHeight = this.appsMenu?.nativeElement?.clientHeight ?? 0;
      }
      if (this.orgMenu) {
        this.orgMenuHeight = this.orgMenu?.nativeElement?.clientHeight ?? 0;
      }
    });
  }

  public logout(): void {
    this.analyticsService.reset();
    this.store$.dispatch(AuthActions.logout({ params: { msg: undefined } }));
  }

  public switchApp({ url }: PartnerAppSelection): void {
    const redirectUrl = new URL(url, window.location.origin).origin;
    const analyticsEvent = resolveAppSwitchAnalyticsEvent(
      redirectUrl,
      new URL(this.controlCenterUrl, window.location.origin).origin,
      new URL(this.webUrl, window.location.origin).origin,
    );
    if (analyticsEvent) {
      this.trackNavigationEvent(analyticsEvent);
    }
    window.open(
      this.universalLoginService.buildLoginUrl(redirectUrl),
      '_blank',
      'noopener,noreferrer',
    );
  }

  public trackNavigationEvent(event: SharedAnalyticsEvent): void {
    combineLatest([this.selectUser$, this.selectActiveOrganization$])
      .pipe(
        filter(
          ([user, organization]) =>
            !!user?._id &&
            !!organization?._id &&
            !!organization.settings?.orgName,
        ),
        take(1),
      )
      .subscribe(([user, organization]) => {
        if (!user?._id || !organization?._id || !organization.settings?.orgName) {
          return;
        }
        this.analyticsService.identify(user._id);
        this.analyticsService.setContext({
          _org_id: organization._id,
          _org_name: organization.settings.orgName,
        });
        this.analyticsService.track(event);
      });
  }

  protected readonly analyticsEvents = SHARED_ANALYTICS_EVENTS;
}
